import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const SecurityTab = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(true);
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);
  const [backupCodes, setBackupCodes] = useState([
    'A1B2-C3D4-E5F6',
    'G7H8-I9J0-K1L2',
    'M3N4-O5P6-Q7R8',
    'S9T0-U1V2-W3X4',
    'Y5Z6-A7B8-C9D0'
  ]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const handlePasswordChange = (e) => {
    e?.preventDefault();
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    console.log('Password changed successfully');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSetupPasskey = () => {
    console.log('Setting up WebAuthn passkey...');
    setPasskeyEnabled(true);
  };

  const handleDisableTotp = () => {
    console.log('Disabling TOTP...');
    setTotpEnabled(false);
  };

  const handleGenerateBackupCodes = () => {
    const newCodes = [
      'E1F2-G3H4-I5J6',
      'K7L8-M9N0-O1P2',
      'Q3R4-S5T6-U7V8',
      'W9X0-Y1Z2-A3B4',
      'C5D6-E7F8-G9H0'
    ];
    setBackupCodes(newCodes);
    setShowBackupCodes(true);
  };

  const downloadBackupCodes = () => {
    const codesText = backupCodes?.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'securevault-backup-codes.txt';
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      {/* Password Change Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Lock" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Change Password</h3>
        </div>
        
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <Input
            label="Current Password"
            type={showPasswords ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e?.target?.value)}
            placeholder="Enter current password"
            required
          />
          
          <Input
            label="New Password"
            type={showPasswords ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e?.target?.value)}
            placeholder="Enter new password"
            description="Must be at least 12 characters with mixed case, numbers, and symbols"
            required
          />
          
          <Input
            label="Confirm New Password"
            type={showPasswords ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e?.target?.value)}
            placeholder="Confirm new password"
            required
          />
          
          <Checkbox
            label="Show passwords"
            checked={showPasswords}
            onChange={(e) => setShowPasswords(e?.target?.checked)}
          />
          
          <Button type="submit" variant="default" className="w-full sm:w-auto">
            Update Password
          </Button>
        </form>
      </div>
      {/* Two-Factor Authentication */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Shield" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h3>
        </div>

        <div className="space-y-6">
          {/* TOTP Setup */}
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Smartphone" size={20} className="text-success" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Authenticator App</h4>
                <p className="text-sm text-muted-foreground">
                  {totpEnabled ? 'Active - Google Authenticator' : 'Not configured'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {totpEnabled && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                  <Icon name="CheckCircle" size={12} className="mr-1" />
                  Enabled
                </span>
              )}
              <Button
                variant={totpEnabled ? "outline" : "default"}
                size="sm"
                onClick={totpEnabled ? handleDisableTotp : () => console.log('Setup TOTP')}
              >
                {totpEnabled ? 'Disable' : 'Setup'}
              </Button>
            </div>
          </div>

          {/* WebAuthn Passkey */}
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Key" size={20} className="text-accent" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Security Key (WebAuthn)</h4>
                <p className="text-sm text-muted-foreground">
                  {passkeyEnabled ? 'Active - Hardware key registered' : 'Use hardware security key or biometrics'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {passkeyEnabled && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                  <Icon name="CheckCircle" size={12} className="mr-1" />
                  Enabled
                </span>
              )}
              <Button
                variant={passkeyEnabled ? "outline" : "default"}
                size="sm"
                onClick={passkeyEnabled ? () => console.log('Remove passkey') : handleSetupPasskey}
              >
                {passkeyEnabled ? 'Remove' : 'Setup'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Backup Recovery Codes */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Download" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Backup Recovery Codes</h3>
        </div>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Recovery codes can be used to access your account if you lose access to your two-factor authentication device.
            Each code can only be used once.
          </p>
          
          {showBackupCodes && (
            <div className="bg-muted p-4 rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-sm">
                {backupCodes?.map((code, index) => (
                  <div key={index} className="p-2 bg-background rounded border">
                    {code}
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadBackupCodes}
                className="mt-4"
                iconName="Download"
                iconPosition="left"
              >
                Download Codes
              </Button>
            </div>
          )}
          
          <div className="flex space-x-3">
            <Button
              variant="default"
              onClick={handleGenerateBackupCodes}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Generate New Codes
            </Button>
            {!showBackupCodes && backupCodes?.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setShowBackupCodes(true)}
                iconName="Eye"
                iconPosition="left"
              >
                View Codes
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Shamir Secret Sharing */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Users" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Emergency Recovery</h3>
        </div>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Set up emergency recovery using Shamir Secret Sharing. Your recovery key will be split into multiple parts
            that can be distributed to trusted contacts.
          </p>
          
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div>
              <h4 className="font-medium text-foreground">Secret Sharing Setup</h4>
              <p className="text-sm text-muted-foreground">Not configured</p>
            </div>
            <Button variant="outline" size="sm">
              Configure
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityTab;